import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchMe } from '@/api/auth';
import { useAuthStore } from '@/stores/auth';
import { AuthShell } from './AuthShell';
import { Alert } from '@/components/ui/Alert';

// Receiver for GS-staff impersonation. The admin app mints a 1-hour
// `acting_as_practice_id` JWT via POST /api/admin/staff/impersonate/:practice_id
// and opens this page in a new tab as `/impersonate#token=<jwt>`.
//
// The token is in the fragment (not the query string) so it never
// reaches the server access log or the Referer header. We pull it out
// client-side, fetch /api/me to populate display state, and then
// redirect to "/" — the rest of PMA renders as if the GS staffer were
// acting as the impersonated practice.
//
// The persistent "acting as" banner (see AppShell.tsx) reads
// acting_as_practice_id off the JWT and stays visible for the entire
// impersonation session.

function readTokenFromFragment(): string | null {
    if (typeof window === 'undefined') return null;
    // window.location.hash is "#token=…" or "" — strip the leading '#'.
    const raw = window.location.hash.startsWith('#')
        ? window.location.hash.slice(1)
        : window.location.hash;
    if (!raw) return null;
    const params = new URLSearchParams(raw);
    return params.get('token');
}

export function ImpersonatePage() {
    const navigate = useNavigate();
    const setSession = useAuthStore((s) => s.setSession);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const token = readTokenFromFragment();
        if (!token) {
            setError('Missing impersonation token. Re-launch from the admin app.');
            return;
        }

        // Stage the token in the auth store so the axios interceptor
        // attaches it to the /api/me request below. We'll overwrite the
        // full session (with user/practice) once /api/me responds.
        useAuthStore.setState({ token });

        let cancelled = false;
        fetchMe()
            .then((me) => {
                if (cancelled) return;
                setSession(token, me.user, me.practice);
                // Strip the token off the URL before any other code can
                // see it — leaving `#token=…` in window.location is a
                // tiny but unnecessary leak (browser history, tab title
                // logging tools, etc.).
                window.history.replaceState(null, '', '/');
                navigate('/', { replace: true });
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                useAuthStore.setState({ token: null, user: null, practice: null });
                const msg =
                    err && typeof err === 'object' && 'message' in err
                        ? String((err as { message: unknown }).message)
                        : 'Failed to load impersonation session';
                setError(msg);
            });

        return () => {
            cancelled = true;
        };
    }, [navigate, setSession]);

    return (
        <AuthShell showNotice={false}>
            <div className="space-y-3">
                <h1 className="text-xl font-semibold text-text">Starting impersonation…</h1>
                {error ? (
                    <Alert variant="danger">{error}</Alert>
                ) : (
                    <p className="text-sm text-text-subtle">
                        Loading the practice context. This will redirect automatically.
                    </p>
                )}
            </div>
        </AuthShell>
    );
}
